import React, { useState, useEffect } from 'react';
import { ContactCollection, CollectionMemberWithContact } from '../types/Collections';
import { Contact } from '../types/Contact';
import { supabase } from '../lib/supabase';
import {
    listContactsCore,
    updateContactCore,
    updateCollectionCore,
    getCollectionCore,
    listCollectionMembersWithContactCore,
    removeCollectionMemberByIdCore,
    bulkAddMembersCore,
    listSavedMessagesForCollectionCore,
    createSavedMessageCore,
    updateSavedMessageCore,
    deleteSavedMessageCore,
    listCollectionMemberCheckmarksCore,
    setCollectionMemberCheckmarkCore,
} from '@gv/core';
import { User } from '@supabase/supabase-js';
import ConfirmationModal from './ConfirmationModal';
import {
    ArrowLeft,
    Users,
    Search,
    Trash2,
    Edit2,
    Star,
    UserMinus,
    Calendar,
    Plus,
    X,
    Check,
    Mail,
    Phone,
    MessageSquare,
    Copy,
    Filter
} from 'lucide-react';
import CustomSelect from './CustomSelect';
import MultiSelect from './MultiSelect';
import { useNotifications } from '../hooks/useNotifications';
import NotificationToast from './NotificationToast';

interface SavedMessage {
    id: string;
    message_content: string;
    tone: 'cold' | 'warm' | 'hot';
    created_at: string;
    updated_at: string;
}

interface CollectionDetailsPageProps {
    collectionId: string;
    user: User;
    onBack: () => void;
    onContactSelect?: (contact: Contact, collectionContext?: string, collectionName?: string, collectionId?: string) => void;
    onCollectionUpdate: () => void;
    onNavigateToContacts?: (collectionId: string, collectionName: string, existingMemberIds: string[]) => void;
}

export default function CollectionDetailsPage({
    collectionId,
    user,
    onBack,
    onContactSelect,
    onCollectionUpdate,
    onNavigateToContacts
}: CollectionDetailsPageProps) {
    const [collection, setCollection] = useState<ContactCollection | null>(null);
    const [members, setMembers] = useState<CollectionMemberWithContact[]>([]);
    const [savedMessages, setSavedMessages] = useState<SavedMessage[]>([]);
    const [loading, setLoading] = useState(true);
    // Single People view; pencil toggles edit mode
    const [isEditingMembers, setIsEditingMembers] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchType, setSearchType] = useState<'all' | 'name' | 'details'>('all');
    const [tierFilter, setTierFilter] = useState<string[]>([]);
    const [investmentLikelihoodFilter, setInvestmentLikelihoodFilter] = useState<string[]>([]);
    const [sortBy, setSortBy] = useState('name');
    const [showSearchFilters, setShowSearchFilters] = useState(false);
    const [removing, setRemoving] = useState<string[]>([]);
    const [checkmarks, setCheckmarks] = useState<Record<string, boolean>>({}); // contact_id -> checked
    const [isEditingDetails, setIsEditingDetails] = useState(false);
    const [isAddingContacts, setIsAddingContacts] = useState(false);
    const [editingMessage, setEditingMessage] = useState<SavedMessage | null>(null);
    const [editedCollection, setEditedCollection] = useState({
        name: '',
        // description removed from UI; we keep value for backward-compat saves if present
        description: '',
        message_context: ''
    });
    const { notification, showNotification, hideNotification } = useNotifications();

    // Confirmation modal state
    const [confirmModal, setConfirmModal] = useState<{
        isOpen: boolean;
        title: string;
        message: string;
        onConfirm: () => void;
        type?: 'danger' | 'warning' | 'default';
    }>({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: () => { },
        type: 'default'
    });

    useEffect(() => {
        try { window.dispatchEvent(new Event('gv:refreshCollectionMembers')); } catch { }
        fetchCollection();
        fetchMembers();
        fetchSavedMessages();
        fetchCheckmarks();
    }, [collectionId]);

    const fetchCollection = async () => {
        try {
            const data = await getCollectionCore(supabase as any, collectionId);
            if (!data) throw new Error('Collection not found');
            setCollection(data as any);
            setEditedCollection({
                name: data.name,
                description: data.description || '',
                message_context: data.message_context || data.description || ''
            });
        } catch (error) {
            console.error('Error fetching collection:', error);
        }
    };

    const fetchMembers = async () => {
        setLoading(true);
        try {
            const data = await listCollectionMembersWithContactCore(supabase as any, collectionId);
            const validMembers = (data || []).filter((m: any) => m.contact) as any as CollectionMemberWithContact[];
            setMembers(validMembers);
        } catch (error) {
            console.error('Error fetching collection members:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchCheckmarks = async () => {
        try {
            const rows = await listCollectionMemberCheckmarksCore(supabase as any, collectionId, user.id);
            const map: Record<string, boolean> = {};
            (rows || []).forEach((row: any) => { map[row.contact_id] = !!row.checked; });
            setCheckmarks(map);
        } catch { /* ignore for now per dev env instruction */ }
    };

    const toggleCheckmark = async (contactId: string) => {
        const current = !!checkmarks[contactId];
        setCheckmarks(prev => ({ ...prev, [contactId]: !current }));
        try {
            await setCollectionMemberCheckmarkCore(supabase as any, { collectionId, userId: user.id, contactId, checked: !current });
        } catch {
            setCheckmarks(prev => ({ ...prev, [contactId]: current }));
        }
    };

    // Listen for external membership changes to refresh members list
    useEffect(() => {
        const handler = () => {
            fetchMembers();
        };
        window.addEventListener('gv:refreshCollectionMembers' as any, handler as any);
        return () => window.removeEventListener('gv:refreshCollectionMembers' as any, handler as any);
    }, [collectionId]);

    // Restore scroll position if we have one saved for this collection
    useEffect(() => {
        // Only attempt after we've finished loading and have rendered members
        if (loading) return;
        try {
            const key = `gv:scroll:collection:${collectionId}`;
            const saved = sessionStorage.getItem(key);
            if (!saved) return;

            // Give the browser a tick to paint the list before restoring
            requestAnimationFrame(() => {
                const container = document.getElementById('main-scroll-container');
                if (container) {
                    container.scrollTo({ top: Number(saved) || 0, behavior: 'instant' as any });
                } else {
                    window.scrollTo({ top: Number(saved) || 0, behavior: 'instant' as any });
                }
                // Clear after restoring so future navigations don't force it
                sessionStorage.removeItem(key);
            });
        } catch { /* ignore */ }
    }, [loading, collectionId, members.length]);

    const fetchSavedMessages = async () => {
        try {
            const data = await listSavedMessagesForCollectionCore(supabase as any, collectionId, user.id);
            setSavedMessages(data as any);
        } catch (error) {
            console.error('Error fetching saved messages:', error);
        }
    };

    const saveMessage = async (messageContent: string, tone: 'cold' | 'warm' | 'hot') => {
        try {
            await createSavedMessageCore(supabase as any, { collectionId, userId: user.id, messageContent, tone });
            fetchSavedMessages();
        } catch (error) {
            console.error('Error saving message:', error);
        }
    };

    const updateMessage = async (messageId: string, messageContent: string) => {
        try {
            await updateSavedMessageCore(supabase as any, messageId, { messageContent });
            fetchSavedMessages();
            setEditingMessage(null);
        } catch (error) {
            console.error('Error updating message:', error);
        }
    };

    const deleteMessage = async (messageId: string) => {
        try {
            await deleteSavedMessageCore(supabase as any, messageId);
            fetchSavedMessages();
        } catch (error) {
            console.error('Error deleting message:', error);
        }
    };

    const saveCollectionDetails = async () => {
        try {
            await updateCollectionCore(supabase as any, collectionId, {
                name: editedCollection.name,
                // Intentionally clear description in DB, use message_context instead
                description: null,
                message_context: editedCollection.message_context,
            });
            if (collection) {
                Object.assign(collection, {
                    name: editedCollection.name,
                    description: null,
                    message_context: editedCollection.message_context
                });
            }
            setIsEditingDetails(false);
            onCollectionUpdate();
            showNotification('Collection details updated successfully!', 'success');
        } catch (error) {
            console.error('Error updating collection:', error);
            showNotification('Failed to update collection details', 'error');
        }
    };

    const handleToggleContactStatus = async (contactId: string, currentStatus: string) => {
        try {
            // Toggle between 'Not Contacted' and 'Called'
            const newStatus = currentStatus === 'Not Contacted' ? 'Called' : 'Not Contacted';
            const now = new Date().toISOString();

            await updateContactCore(supabase as any, contactId, {
                status: newStatus as any,
                last_contacted: newStatus === 'Called' ? now : null,
            } as any);

            // Update local state
            setMembers(prev => prev.map(member => {
                if (member.contact.id === contactId) {
                    return {
                        ...member,
                        contact: {
                            ...member.contact,
                            status: newStatus as any,
                            last_contacted: newStatus === 'Called' ? now : null
                        }
                    };
                }
                return member;
            }));
        } catch (error) {
            console.error('Error updating contact status:', error);
            showNotification('Failed to update contact status', 'error');
        }
    };

    const handleRemoveMember = async (memberId: string, contactName: string) => {
        setConfirmModal({
            isOpen: true,
            title: 'Remove Contact',
            message: `Are you sure you want to remove ${contactName} from this collection?`,
            type: 'warning',
            onConfirm: () => confirmRemoveMember(memberId)
        });
    };

    const confirmRemoveMember = async (memberId: string) => {
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
        setRemoving(prev => [...prev, memberId]);
        try {
            await removeCollectionMemberByIdCore(supabase as any, memberId);
            setMembers(prev => prev.filter(m => m.id !== memberId));
            onCollectionUpdate();
        } catch (error) {
            console.error('Error removing member:', error);
            console.error('Error removing member:', error);
            // Could show toast notification here instead of alert
        } finally {
            setRemoving(prev => prev.filter(id => id !== memberId));
        }
    };

    const handleContactClick = (contact: Contact) => {
        if (onContactSelect && collection) {
            onContactSelect(contact, collection.message_context || undefined, collection.name, collectionId);
        }
    };

    const addSingleContact = async (contactId: string) => {
        try {
            await bulkAddMembersCore(supabase as any, [{ collection_id: collectionId, contact_id: contactId, added_by: user.id }]);
            fetchMembers();
        } catch (error) {
            console.error('Error adding contact:', error);
            showNotification('Failed to add contact to collection', 'error');
        }
    };

    const filteredMembers = members.filter(member => {
        const searchLower = searchTerm.toLowerCase();
        const contact = member.contact;

        // Search filter
        if (searchTerm !== '') {
            let searchMatch = false;

            if (searchType === 'name') {
                // Search only name-related fields
                searchMatch = (
                    `${contact.first_name} ${contact.last_name}`.toLowerCase().includes(searchLower) ||
                    (contact.nickname || '').toLowerCase().includes(searchLower)
                );
            } else if (searchType === 'details') {
                // Search location/detail fields but exclude names
                searchMatch = (
                    (contact.company || '').toLowerCase().includes(searchLower) ||
                    (contact.email || '').toLowerCase().includes(searchLower) ||
                    (contact.title || '').toLowerCase().includes(searchLower) ||
                    (contact.industry || '').toLowerCase().includes(searchLower)
                );
            } else {
                // Search all fields (default)
                searchMatch = (
                    `${contact.first_name} ${contact.last_name}`.toLowerCase().includes(searchLower) ||
                    (contact.company || '').toLowerCase().includes(searchLower) ||
                    (contact.email || '').toLowerCase().includes(searchLower) ||
                    (contact.title || '').toLowerCase().includes(searchLower) ||
                    (contact.industry || '').toLowerCase().includes(searchLower) ||
                    (contact.nickname || '').toLowerCase().includes(searchLower)
                );
            }

            if (!searchMatch) return false;
        }

        // Tier filter
        if (tierFilter.length > 0) {
            const contactTier = contact.tier || 'Unassigned';
            if (!tierFilter.includes(contactTier)) return false;
        }

        // Investment likelihood filter
        if (investmentLikelihoodFilter.length > 0) {
            const likelihood = contact.investment_likelihood_score?.toString() || 'none';
            if (!investmentLikelihoodFilter.includes(likelihood)) return false;
        }

        return true;
    });

    // Sort the filtered results
    const sortedMembers = [...filteredMembers].sort((a, b) => {
        const contactA = a.contact;
        const contactB = b.contact;

        switch (sortBy) {
            case 'name':
                return `${contactA.first_name} ${contactA.last_name}`.localeCompare(`${contactB.first_name} ${contactB.last_name}`);
            case 'recent':
                return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
            case 'tier':
                const tierOrder = { 'A': 1, 'B': 2, 'C': 3, 'D': 4, 'Unassigned': 5 };
                const tierA = tierOrder[contactA.tier as keyof typeof tierOrder] || 5;
                const tierB = tierOrder[contactB.tier as keyof typeof tierOrder] || 5;
                return tierA - tierB;
            case 'likelihood':
                const likelihoodA = contactA.investment_likelihood_score || 0;
                const likelihoodB = contactB.investment_likelihood_score || 0;
                return likelihoodB - likelihoodA; // Highest first
            default:
                return 0;
        }
    });

    const getTierColor = (tier: string) => {
        switch (tier) {
            case 'A': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            case 'B': return 'bg-blue-100 text-blue-800 border-blue-200';
            case 'C': return 'bg-green-100 text-green-800 border-green-200';
            case 'D': return 'bg-gray-100 text-gray-800 border-gray-200';
            default: return 'bg-slate-100 text-slate-800 border-slate-200';
        }
    };

    const getContactLocation = (contact: Contact) => {
        // Priority order for location information
        if (contact.state) return contact.state;
        if (contact.address_1_region) return contact.address_1_region;
        if (contact.business_state) return contact.business_state;
        if (contact.city && contact.state) return `${contact.city}, ${contact.state}`;
        if (contact.address_1_city && contact.address_1_region) return `${contact.address_1_city}, ${contact.address_1_region}`;
        if (contact.business_city && contact.business_state) return `${contact.business_city}, ${contact.business_state}`;
        if (contact.city) return contact.city;
        if (contact.address_1_city) return contact.address_1_city;
        if (contact.business_city) return contact.business_city;
        if (contact.location_info) return contact.location_info;
        return null;
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    if (!collection) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-900">
            {notification && (
                <NotificationToast notification={notification} onClose={hideNotification} />
            )}
            {/* Clean Header */}
            <div className="bg-gradient-to-b from-gray-800 to-gray-900 border-b border-gray-700 sticky top-0 z-30">
                {/* Top section - Back + title + actions */}
                <div className="px-4 md:px-6 py-4">
                    <div className="flex items-center justify-between">
                        <button
                            onClick={onBack}
                            className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors group"
                        >
                            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                            <span className="text-sm font-medium">Back</span>
                        </button>
                        <div className="flex items-center gap-2">
                            {/* Icon-only add button */}
                            <button
                                onClick={() => {
                                    if (onNavigateToContacts) {
                                        const existingMemberIds = members.map(member => member.contact.id);
                                        onNavigateToContacts(collectionId, collection?.name || 'Collection', existingMemberIds);
                                    } else {
                                        setIsAddingContacts(true);
                                    }
                                }}
                                className="hidden sm:inline-flex items-center justify-center p-2 rounded-md bg-gray-100 text-black hover:bg-white"
                                title="Add Contacts"
                                aria-label="Add Contacts"
                            >
                                <Plus className="w-4 h-4" />
                            </button>
                            {/* Icon-only edit toggle */}
                            <button
                                onClick={() => setIsEditingMembers(v => !v)}
                                className={`hidden sm:inline-flex items-center justify-center p-2 rounded-md border ${isEditingMembers ? 'bg-gray-800 text-white border-gray-600' : 'bg-gray-900 text-gray-200 border-gray-700 hover:bg-gray-800'}`}
                                title={isEditingMembers ? 'Exit Edit Mode' : 'Edit Members'}
                                aria-label="Toggle Edit Mode"
                            >
                                <Edit2 className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    <div className="mt-3">
                        <h1 className="text-xl md:text-2xl font-semibold text-white break-words">
                            {collection.name}
                        </h1>
                        <p className="text-gray-400 text-sm mt-1">
                            {members.length} contact{members.length !== 1 ? 's' : ''}
                        </p>
                    </div>
                </div>
                {/* People controls */}
                {
                    <div className="px-4 md:px-6 py-4">
                        {/* Search Bar with integrated filter */}
                        <div className="flex items-center space-x-2">
                            <div className="flex-1 relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                <input
                                    type="text"
                                    placeholder="Search"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-12 py-3 bg-gray-800 border border-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 rounded-lg transition-all"
                                />
                                {/* Filter button inside search bar */}
                                <button
                                    onClick={() => setShowSearchFilters(!showSearchFilters)}
                                    className={`absolute right-2 top-1/2 transform -translate-y-1/2 p-1.5 rounded-md transition-colors ${showSearchFilters
                                        ? 'bg-blue-600 text-white'
                                        : 'text-gray-400 hover:text-white hover:bg-gray-700'
                                        }`}
                                    title="Toggle filters"
                                >
                                    <Filter className="w-4 h-4" />
                                </button>
                            </div>
                        </div>

                        {/* Search Filters */}
                        {showSearchFilters && (
                            <div className="mt-4 bg-gray-800/50 border border-gray-700 rounded-lg p-4 space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-sm font-medium text-gray-300 block mb-2">Search in:</label>
                                        <CustomSelect
                                            options={[
                                                { value: 'all', label: 'All Fields' },
                                                { value: 'name', label: 'Names Only' },
                                                { value: 'details', label: 'Details Only' }
                                            ]}
                                            value={searchType}
                                            onChange={setSearchType}
                                        />
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium text-gray-300 block mb-2">Sort by:</label>
                                        <CustomSelect
                                            options={[
                                                { value: 'name', label: 'Name (A-Z)' },
                                                { value: 'recent', label: 'Recently Added' },
                                                { value: 'tier', label: 'Tier (A-D)' },
                                                { value: 'likelihood', label: 'Likelihood (High-Low)' }
                                            ]}
                                            value={sortBy}
                                            onChange={setSortBy}
                                        />
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium text-gray-300 block mb-2">Tier:</label>
                                        <MultiSelect
                                            options={[
                                                { value: 'A', label: 'Tier A' },
                                                { value: 'B', label: 'Tier B' },
                                                { value: 'C', label: 'Tier C' },
                                                { value: 'D', label: 'Tier D' },
                                                { value: 'Unassigned', label: 'Unassigned' }
                                            ]}
                                            value={tierFilter}
                                            onChange={setTierFilter}
                                            placeholder="All Tiers"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium text-gray-300 block mb-2">Investment Likelihood:</label>
                                        <MultiSelect
                                            options={[
                                                { value: '10', label: '10 (Highest)' },
                                                { value: '9', label: '9' },
                                                { value: '8', label: '8' },
                                                { value: '7', label: '7' },
                                                { value: '6', label: '6' },
                                                { value: '5', label: '5' },
                                                { value: '4', label: '4' },
                                                { value: '3', label: '3' },
                                                { value: '2', label: '2' },
                                                { value: '1', label: '1 (Lowest)' },
                                                { value: 'none', label: 'No Score' }
                                            ]}
                                            value={investmentLikelihoodFilter}
                                            onChange={setInvestmentLikelihoodFilter}
                                            placeholder="All Likelihood"
                                        />
                                    </div>
                                </div>

                                {(tierFilter.length > 0 || investmentLikelihoodFilter.length > 0 || searchType !== 'all' || sortBy !== 'name') && (
                                    <div className="pt-2 border-t border-gray-700">
                                        <button
                                            onClick={() => {
                                                setTierFilter([]);
                                                setInvestmentLikelihoodFilter([]);
                                                setSearchType('all');
                                                setSortBy('name');
                                            }}
                                            className="text-sm text-blue-400 hover:text-blue-300 transition-colors font-medium"
                                        >
                                            Clear all filters
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                }
            </div>

            {/* People Content - Show only in People tab */}
            {
                <div className="flex-1">
                    {loading ? (
                        <div className="flex items-center justify-center h-64">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                        </div>
                    ) : sortedMembers.length > 0 ? (
                        /* Contact Cards - Exactly like main contacts: 2 per row */
                        <div className="grid grid-cols-2 gap-3 md:gap-6 p-3 md:p-6">
                            {sortedMembers.map((member) => {
                                const contact = member.contact;
                                const isRemoving = removing.includes(member.id);

                                return (
                                    <button
                                        key={member.id}
                                        onClick={() => handleContactClick(contact)}
                                        disabled={isRemoving}
                                        className={`bg-black border border-gray-800 p-3 md:p-6 hover:bg-gray-900 transition-all duration-200 text-left group rounded-lg relative ${isRemoving ? 'opacity-50' : ''}`}
                                    >
                                        {/* Layout exactly like main contacts */}
                                        <div className="flex flex-col md:flex-row md:items-center md:space-x-6 space-y-3 md:space-y-0">
                                            {/* Large Contact Image */}
                                            <div className="w-28 h-28 md:w-32 md:h-32 lg:w-36 lg:h-36 bg-gray-700 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0 group-hover:scale-105 transition-transform mx-auto md:mx-0 shadow-lg relative">
                                                {contact.profile_image_url ? (
                                                    <img
                                                        src={contact.profile_image_url}
                                                        alt={`${contact.first_name} ${contact.last_name}`}
                                                        className="w-full h-full object-cover"
                                                    />
                                                ) : (
                                                    <span className="text-gray-300 font-semibold text-xl md:text-2xl lg:text-3xl">
                                                        {contact.first_name?.[0]}{contact.last_name?.[0]}
                                                    </span>
                                                )}
                                                {/* Non-intrusive per-collection done marker */}
                                                {checkmarks[contact.id] && (
                                                    <div className="absolute -top-1 -right-1 bg-green-600 text-white text-[10px] rounded-full px-1.5 py-0.5 shadow border border-green-400/50">Done</div>
                                                )}
                                                <button
                                                    type="button"
                                                    title={checkmarks[contact.id] ? 'Mark as not done' : 'Mark as done for this collection'}
                                                    onClick={(e) => { e.stopPropagation(); toggleCheckmark(contact.id); }}
                                                    className="absolute bottom-1 right-1 p-1 rounded-full bg-black/50 hover:bg-black/70 border border-gray-700"
                                                >
                                                    <Check className={`w-4 h-4 ${checkmarks[contact.id] ? 'text-green-400' : 'text-gray-300'}`} />
                                                </button>
                                            </div>

                                            {/* Name and Details - Text wraps to match image width */}
                                            <div className="flex-1 min-w-0 text-center md:text-left">
                                                {/* Name - Mobile: Text width matches image, Desktop: Flexible */}
                                                <h3 className="w-28 md:w-auto mx-auto md:mx-0 text-sm md:text-xl lg:text-2xl font-light text-white mb-1 leading-tight break-words">
                                                    <span
                                                        className="inline-block cursor-pointer hover:opacity-80"
                                                        title="Click to copy name"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            const name = `${contact.first_name || ''} ${contact.last_name || ''}`.trim();
                                                            if (name) {
                                                                navigator.clipboard?.writeText(name).then(() => {
                                                                    showNotification(`Copied \"${name}\"`, 'success');
                                                                }).catch(() => {
                                                                    showNotification('Failed to copy', 'error');
                                                                });
                                                            }
                                                        }}
                                                    >
                                                        {contact.first_name} {contact.last_name}
                                                    </span>
                                                </h3>

                                                {/* Company */}
                                                {contact.company && (
                                                    <p className="w-28 md:w-auto mx-auto md:mx-0 text-xs md:text-sm text-gray-400 mb-1 md:mb-2 break-words">
                                                        <span
                                                            className="inline-block cursor-pointer hover:text-gray-300"
                                                            title="Click to copy company"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                const company = contact.company || '';
                                                                if (company) {
                                                                    navigator.clipboard?.writeText(company).then(() => {
                                                                        showNotification(`Copied \"${company}\"`, 'success');
                                                                    }).catch(() => showNotification('Failed to copy', 'error'));
                                                                }
                                                            }}
                                                        >
                                                            {contact.company}
                                                        </span>
                                                    </p>
                                                )}

                                                {/* Location Display - Same as main contacts */}
                                                {getContactLocation(contact) && (
                                                    <p className="w-28 md:w-auto mx-auto md:mx-0 text-xs md:text-sm text-gray-400 mb-1 md:mb-2 break-words">
                                                        <span
                                                            className="inline-block cursor-pointer hover:text-gray-300"
                                                            title="Click to copy location"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                const loc = getContactLocation(contact) || '';
                                                                if (loc) {
                                                                    navigator.clipboard?.writeText(loc).then(() => {
                                                                        showNotification(`Copied \"${loc}\"`, 'success');
                                                                    }).catch(() => showNotification('Failed to copy', 'error'));
                                                                }
                                                            }}
                                                        >
                                                            {getContactLocation(contact)}
                                                        </span>
                                                    </p>
                                                )}

                                                {/* Show priority star only if contact is priority */}
                                                {contact.is_priority && (
                                                    <div className="flex justify-center md:justify-start">
                                                        <Star className="w-4 h-4 md:w-5 md:h-5 text-yellow-400 fill-current" />
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Action Buttons - Positioned in top right */}
                                        <div className="absolute top-2 right-2 flex space-x-1">
                                            {/* Remove Button - Only show in edit mode */}
                                            {isEditingMembers && (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleRemoveMember(member.id, `${contact.first_name} ${contact.last_name}`);
                                                    }}
                                                    disabled={isRemoving}
                                                    className="p-2 text-gray-400 hover:text-red-400 transition-colors disabled:opacity-50 bg-gray-900 rounded-lg"
                                                    title="Remove from Collection"
                                                >
                                                    {isRemoving ? (
                                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-400"></div>
                                                    ) : (
                                                        <UserMinus className="w-4 h-4" />
                                                    )}
                                                </button>
                                            )}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="text-center py-12 px-4">
                            <Users className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                            <h3 className="text-lg font-light text-white mb-2">
                                {searchTerm ? 'No contacts found' : 'No contacts in this collection'}
                            </h3>
                            <p className="text-gray-400 font-light mb-6">
                                {searchTerm
                                    ? 'Try adjusting your search terms'
                                    : 'Add contacts to this collection to get started'
                                }
                            </p>
                            {!searchTerm && (
                                <button
                                    onClick={() => setIsAddingContacts(true)}
                                    className="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                                >
                                    <Plus className="w-5 h-5 mr-2" />
                                    Add Contacts
                                </button>
                            )}
                        </div>
                    )}
                </div>
            }

            {/* Add Contacts Modal */}
            {isAddingContacts && (
                <AddContactsModal
                    collectionId={collectionId}
                    user={user}
                    onClose={() => setIsAddingContacts(false)}
                    onContactsAdded={() => {
                        fetchMembers();
                        setIsAddingContacts(false);
                    }}
                />
            )}

            {/* Confirmation Modal */}
            <ConfirmationModal
                isOpen={confirmModal.isOpen}
                title={confirmModal.title}
                message={confirmModal.message}
                type={confirmModal.type}
                onConfirm={confirmModal.onConfirm}
                onCancel={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
                confirmText="Remove"
                cancelText="Cancel"
            />
        </div>
    );
}

// Add Contacts Modal Component
interface AddContactsModalProps {
    collectionId: string;
    user: User;
    onClose: () => void;
    onContactsAdded: () => void;
}

function AddContactsModal({ collectionId, user, onClose, onContactsAdded }: AddContactsModalProps) {
    const [availableContacts, setAvailableContacts] = useState<Contact[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const { notification, showNotification, hideNotification } = useNotifications();

    useEffect(() => {
        fetchAvailableContacts();
    }, []);

    const fetchAvailableContacts = async () => {
        setLoading(true);
        try {
            // Get all user contacts via core
            const allContacts = await listContactsCore(supabase as any, { includeArchived: false, limit: 1000, offset: 0 });

            // Get existing collection members
            const existingMembers = await listCollectionMembersWithContactCore(supabase as any, collectionId);

            // Filter out contacts already in collection
            const existingContactIds = new Set((existingMembers || []).map((m: any) => m.contact_id));
            const available = allContacts.filter(contact => !existingContactIds.has(contact.id));
            setAvailableContacts(available as any);
        } catch (error) {
            console.error('Error fetching contacts:', error);
        } finally {
            setLoading(false);
        }
    };

    const filteredContacts = availableContacts.filter((contact: any) => {
        const searchLower = searchTerm.toLowerCase();
        return (
            `${contact.first_name} ${contact.last_name}`.toLowerCase().includes(searchLower) ||
            (contact.company || '').toLowerCase().includes(searchLower) ||
            (contact.email || '').toLowerCase().includes(searchLower)
        );
    });

    const addSingleContact = async (contactId: string) => {
        try {
            await bulkAddMembersCore(supabase as any, [{ collection_id: collectionId, contact_id: contactId, added_by: user.id }]);
            // Remove from available contacts
            setAvailableContacts(prev => prev.filter(c => c.id !== contactId));
        } catch (error) {
            console.error('Error adding contact:', error);
            showNotification('Failed to add contact to collection', 'error');
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-end sm:items-center justify-center z-40">
            <div className="bg-gray-900 border border-gray-700 rounded-t-lg sm:rounded-lg w-full max-w-2xl max-h-[80vh] sm:max-h-[70vh] mx-4 mb-0 sm:mb-4 flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-700">
                    <h3 className="text-lg sm:text-xl font-light text-white">Add Contacts</h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-white transition-colors"
                    >
                        ×
                    </button>
                </div>

                {/* Search */}
                <div className="p-4 sm:p-6 border-b border-gray-700">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <input
                            type="text"
                            placeholder="Search contacts to add..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-600 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 rounded-lg"
                        />
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto">
                    {loading ? (
                        <div className="flex items-center justify-center h-32">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                        </div>
                    ) : filteredContacts.length > 0 ? (
                        <div className="divide-y divide-gray-700">
                            {filteredContacts.map((contact) => (
                                <div key={contact.id} className="p-4 hover:bg-gray-800 transition-colors">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-3 flex-1">
                                            {/* Profile Image */}
                                            <div className="w-10 h-10 bg-gray-700 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0 relative">
                                                {contact.profile_image_url ? (
                                                    <img
                                                        src={contact.profile_image_url}
                                                        alt={`${contact.first_name} ${contact.last_name}`}
                                                        className="w-full h-full object-cover"
                                                    />
                                                ) : (
                                                    <div className="text-gray-400 text-sm font-medium">
                                                        {contact.first_name?.[0]}{contact.last_name?.[0]}
                                                    </div>
                                                )}

                                                {/* Green overlay for contacted status */}
                                                {contact.is_contacted && (
                                                    <div className="absolute inset-0 bg-green-500/20 border border-green-400/30 rounded-lg"></div>
                                                )}
                                            </div>

                                            {/* Contact Info */}
                                            <div className="flex-1 min-w-0 pr-2">
                                                <h4 className="text-white font-medium truncate">
                                                    {contact.first_name} {contact.last_name}
                                                </h4>
                                                <p className="text-gray-400 text-sm truncate">
                                                    {contact.title && contact.company ?
                                                        `${contact.title} at ${contact.company}` :
                                                        contact.company || contact.title || contact.email || 'No additional info'
                                                    }
                                                </p>
                                            </div>
                                        </div>

                                        {/* Add Button */}
                                        <button
                                            onClick={() => addSingleContact(contact.id)}
                                            className="flex items-center px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg transition-colors"
                                        >
                                            <Plus className="w-4 h-4 mr-1" />
                                            Add
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center h-32 text-gray-400">
                            <Users className="w-8 h-8 mb-2" />
                            <p className="text-sm">{searchTerm ? 'No matching contacts found' : 'All contacts are already in this collection'}</p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex justify-end p-4 sm:p-6 border-t border-gray-700">
                    <button
                        onClick={onContactsAdded}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                    >
                        Done
                    </button>
                </div>
            </div>
        </div>
    );
}
